/**
 * Admin · App settings (§21 / §B-9). The global Settings singleton: weight
 * tolerance, retention windows, pack-lock idle TTL, address provider, shortage
 * notifications, packing-slip languages, station-trust window. ADMIN edits +
 * saves (optimistic concurrency); MANAGER sees it read-only. The lock idle TTL
 * is stored in ms but shown/edited in MINUTES (conversion centralized here).
 */
import { useEffect, useState } from "react";
import { ApiError, api, type AdminSettings, type SettingsInput } from "../../lib/api.js";
import { NOTIFICATION_CHANNELS, type NotificationChannel } from "@cairn/shared";
import { useResource } from "../../lib/useResource.js";
import { useSession } from "../../components/auth.js";
import { Field, Input, Select, Button, ErrorState, SkeletonRows, useToast } from "../../components/ui/index.js";

const msToMin = (ms: number) => Math.round(ms / 60000);
const minToMs = (min: number) => Math.round(min * 60000);

export function Settings() {
  const { user } = useSession();
  const isAdmin = user?.role === "ADMIN";
  const r = useResource(api.admin.settings);
  const { toast } = useToast();

  const [form, setForm] = useState<AdminSettings | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (r.data?.settings) setForm(r.data.settings);
  }, [r.data]);

  const set = <K extends keyof AdminSettings>(key: K, value: AdminSettings[K]) =>
    setForm((f) => (f ? { ...f, [key]: value } : f));
  const num = (key: keyof AdminSettings) => (e: React.ChangeEvent<HTMLInputElement>) => set(key, (Number(e.target.value) || 0) as never);

  async function save() {
    if (!form) return;
    const input: SettingsInput = {
      weightTolerancePercent: form.weightTolerancePercent,
      weightToleranceFloorG: form.weightToleranceFloorG,
      photoRetentionDays: form.photoRetentionDays,
      labelRetentionDays: form.labelRetentionDays,
      lockIdleTtlMs: form.lockIdleTtlMs,
      addressProvider: form.addressProvider.trim() || "GOOGLE",
      shortagePolicy: form.shortagePolicy,
      shortageNotifyChannel: form.shortageNotifyChannel,
      shortageNotifyTarget: form.shortageNotifyTarget?.trim() || null,
      slipLanguages: form.slipLanguages.map((l) => l.trim()).filter(Boolean),
      stationTrustDays: form.stationTrustDays,
    };
    setSaving(true);
    try {
      const res = await api.admin.updateSettings(input, form.updatedAt);
      setForm(res.settings);
      toast("Settings saved", "success");
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        toast("Settings changed since you loaded them — reloading.", "danger");
        r.reload();
      } else if (e instanceof ApiError && e.status === 400) {
        toast((e.detail as { error?: string } | undefined)?.error ?? "Invalid settings.", "danger");
      } else if (e instanceof ApiError && e.status === 403) {
        toast("Admin role required.", "danger");
      } else {
        toast("Couldn’t save settings.", "danger");
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="admin">
      <div className="page-head">
        <div className="page-head__title">
          <h1>App settings</h1>
        </div>
        {!isAdmin && <span className="soon" title="Admin role required to edit">Read-only · admin can edit</span>}
      </div>

      {r.error ? (
        <ErrorState title="Couldn’t load settings" hint="Retry in a moment." onRetry={r.reload} />
      ) : !form ? (
        <div className="table-card"><SkeletonRows rows={6} cols={2} /></div>
      ) : (
        <fieldset className="settings-form" disabled={!isAdmin}>
          <div className="rule-form__row2">
            <Field label="Weight tolerance (%)">
              <Input value={String(form.weightTolerancePercent)} onChange={num("weightTolerancePercent")} inputMode="numeric" />
            </Field>
            <Field label="Weight tolerance floor (g)">
              <Input value={String(form.weightToleranceFloorG)} onChange={num("weightToleranceFloorG")} inputMode="numeric" />
            </Field>
          </div>

          <div className="rule-form__row2">
            <Field label="Pack-lock idle timeout (minutes)">
              <Input
                value={String(msToMin(form.lockIdleTtlMs))}
                onChange={(e) => set("lockIdleTtlMs", minToMs(Number(e.target.value) || 0))}
                inputMode="numeric"
              />
            </Field>
            <Field label="Station-trust window (days)">
              <Input value={String(form.stationTrustDays)} onChange={num("stationTrustDays")} inputMode="numeric" />
            </Field>
          </div>

          <div className="rule-form__row2">
            <Field label="Photo retention (days)">
              <Input value={String(form.photoRetentionDays)} onChange={num("photoRetentionDays")} inputMode="numeric" />
            </Field>
            <Field label="Label retention (days)">
              <Input value={String(form.labelRetentionDays)} onChange={num("labelRetentionDays")} inputMode="numeric" />
            </Field>
          </div>

          <div className="rule-form__row2">
            <Field label="Address provider">
              <Input value={form.addressProvider} onChange={(e) => set("addressProvider", e.target.value)} placeholder="GOOGLE" />
            </Field>
            <Field label="Packing-slip languages">
              <Input
                value={form.slipLanguages.join(", ")}
                onChange={(e) => set("slipLanguages", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                placeholder="sl, en"
              />
            </Field>
          </div>

          <div className="rule-form__row2">
            <Field label="Shortage policy (default)" hint="Default when an item is short; a store can override this.">
              <Select
                ariaLabel="Shortage policy"
                value={form.shortagePolicy}
                onChange={(v) => set("shortagePolicy", v)}
                options={[{ value: "HOLD_WHOLE", label: "Hold whole order" }, { value: "SHIP_PARTIAL", label: "Ship partial" }]}
              />
            </Field>
            <Field label="Shortage notify channel">
              <Select
                ariaLabel="Shortage notify channel"
                value={form.shortageNotifyChannel}
                onChange={(v) => set("shortageNotifyChannel", v as NotificationChannel)}
                options={NOTIFICATION_CHANNELS.map((c) => ({ value: c, label: c.charAt(0) + c.slice(1).toLowerCase() }))}
              />
            </Field>
          </div>

          <div className="rule-form__row2">
            <Field label="Shortage notify target">
              <Input value={form.shortageNotifyTarget ?? ""} onChange={(e) => set("shortageNotifyTarget", e.target.value || null)} placeholder="#ops-shortages / email" />
            </Field>
          </div>

          {isAdmin && (
            <div className="rule-form__actions">
              <Button variant="primary" icon="check" onClick={() => void save()} disabled={saving}>
                {saving ? "Saving…" : "Save settings"}
              </Button>
            </div>
          )}
        </fieldset>
      )}
    </section>
  );
}
